
import mammoth from "mammoth";
import { jsPDF } from "jspdf";

/**
 * Converts a Word (.docx) file to a PDF blob using mammoth and jsPDF.
 * @param {File} file - The .docx file to convert.
 * @returns {Promise<Blob>} The generated PDF as a Blob.
 */
export const convertWordToPdf = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const arrayBuffer = event.target.result;
        
        // Extract raw text from the .docx file
        // We use extractRawText to get plain text.
        // Note: Preserving exact layout/styles client-side without massive libraries is limited.
        // We will attempt to preserve paragraphs.
        const result = await mammoth.extractRawText({ arrayBuffer: arrayBuffer });
        const text = result.value; // The raw text
        const messages = result.messages; // Any warnings

        if (messages.length > 0) {
          console.log("Mammoth messages:", messages);
        }

        // Generate PDF
        const doc = new jsPDF();
        
        // Add text to PDF with basic wrapping
        const splitText = doc.splitTextToSize(text, 180); // Width limited to 180mm (A4 is ~210mm)
        
        // Check if text is too long for one page
        // Simple implementation: just add text. jsPDF handles auto-paging for simple text add? 
        // No, invalid addText call might not auto-page perfectly in all versions of jsPDF 
        // without logic. But doc.text() usually handles multi-line if we give it an array.
        // Let's go page by page or just let jsPDF handle it.
        // Actually, for long text, we need to handle paging manually or use a plugin.
        // A simple approach for this MVP:
        
        let y = 10;
        const pageHeight = doc.internal.pageSize.height;
        const margin = 10;
        const lineHeight = 7;

        splitText.forEach(line => {
            if (y > pageHeight - margin) {
                doc.addPage();
                y = margin;
            }
            doc.text(line, margin, y);
            y += lineHeight;
        });

        const pdfBlob = doc.output("blob");
        resolve(pdfBlob);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
};
